// Utilities Report View Component
// Displays the last 6 months of utilities data with averages
// Months as rows, metrics as columns
// UPDATED: Added Print and Export to Excel buttons

import React, { useState, useEffect } from 'react';
import { utilitiesAPI, flashReportAPI } from '../utils/api';
import '../styles/UtilitiesReportView.css';

function UtilitiesReportView() {
    const [plants, setPlants] = useState([]);
    const [selectedPlant, setSelectedPlant] = useState('');
    const [endingMonth, setEndingMonth] = useState('');
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ text: '', type: '' });

    // Load plants when component mounts
    useEffect(() => {
        loadPlants();
        const now = new Date();
        const monthStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        setEndingMonth(monthStr);
    }, []);

    // Load report data when plant or ending month changes
    useEffect(() => {
        if (selectedPlant && endingMonth) {
            loadReportData();
        }
    }, [selectedPlant, endingMonth]);

    // Load plants from API
    const loadPlants = async () => {
        try {
            const data = await flashReportAPI.getPlants();
            setPlants(data.plants);
            const seattle = data.plants.find(p => p.plant_name === 'Seattle');
            if (seattle) {
                setSelectedPlant(seattle.plant_id.toString());
            }
        } catch (error) {
            setMessage({ text: 'Error loading plants', type: 'error' });
        }
    };

    // Load 6 months of data for selected plant
    const loadReportData = async () => {
        if (!selectedPlant || !endingMonth) return;
        try {
            setLoading(true);
            setMessage({ text: '', type: '' });
            const monthDate = `${endingMonth}-01`;
            const data = await utilitiesAPI.getHistory(selectedPlant, 6, monthDate);
            if (data.reports && data.reports.length > 0) {
                setReportData(data.reports);
            } else {
                setReportData([]);
                setMessage({ text: 'No data available for this plant', type: 'info' });
            }
        } catch (error) {
            setMessage({ text: 'Error loading report data', type: 'error' });
            setReportData([]);
        } finally {
            setLoading(false);
        }
    };

    // Handle Excel export — calls the backend export route
    const handleExportExcel = async () => {
        if (!selectedPlant || !endingMonth) {
            setMessage({ text: 'Please select a plant and month', type: 'error' });
            return;
        }
        try {
            setLoading(true);
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/export/utilities-6month-view/${selectedPlant}/${endingMonth}`,
                { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
            );
            if (!response.ok) throw new Error('Export failed');
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Utilities_6Month_${selectedPlantName}_${endingMonth}.xlsx`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            setMessage({ text: 'Report exported successfully', type: 'success' });
        } catch (error) {
            console.error('Export error:', error);
            setMessage({ text: 'Error exporting report', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    // Calculate 6-month averages (matches original logic exactly)
    const calculateAverages = () => {
        if (reportData.length === 0) return null;
        const sum = reportData.reduce((acc, row) => ({
            pounds:                acc.pounds                + (parseFloat(row.pounds)                || 0),
            water_gallons:         acc.water_gallons         + (parseFloat(row.water_gallons)         || 0),
            water_cost_per_gallon: acc.water_cost_per_gallon + (parseFloat(row.water_cost_per_gallon) || 0),
            water_total_cost:      acc.water_total_cost      + (parseFloat(row.water_total_cost)      || 0),
            gallon_per_lb:         acc.gallon_per_lb         + (parseFloat(row.gallon_per_lb)         || 0),
            gas_therms:            acc.gas_therms            + (parseFloat(row.gas_therms)            || 0),
            gas_cost_per_therm:    acc.gas_cost_per_therm    + (parseFloat(row.gas_cost_per_therm)    || 0),
            gas_total_cost:        acc.gas_total_cost        + (parseFloat(row.gas_total_cost)        || 0),
            therm_per_lb:          acc.therm_per_lb          + (parseFloat(row.therm_per_lb)          || 0),
            electric_kwh:          acc.electric_kwh          + (parseFloat(row.electric_kwh)          || 0),
            electric_cost_per_kwh: acc.electric_cost_per_kwh + (parseFloat(row.electric_cost_per_kwh) || 0),
            electric_total_cost:   acc.electric_total_cost   + (parseFloat(row.electric_total_cost)   || 0),
            kwh_per_lb:            acc.kwh_per_lb            + (parseFloat(row.kwh_per_lb)            || 0)
        }), {
            pounds: 0, water_gallons: 0, water_cost_per_gallon: 0, water_total_cost: 0,
            gallon_per_lb: 0, gas_therms: 0, gas_cost_per_therm: 0, gas_total_cost: 0,
            therm_per_lb: 0, electric_kwh: 0, electric_cost_per_kwh: 0,
            electric_total_cost: 0, kwh_per_lb: 0
        });
        const count = reportData.length;
        const avgPounds            = sum.pounds            / count;
        const avgWaterGallons      = sum.water_gallons     / count;
        const avgWaterCostPerGallon = sum.water_cost_per_gallon / count;
        const avgGasTherms         = sum.gas_therms        / count;
        const avgGasCostPerTherm   = sum.gas_cost_per_therm / count;
        const avgElectricKwh       = sum.electric_kwh      / count;
        const avgElectricCostPerKwh = sum.electric_cost_per_kwh / count;
        return {
            pounds:                avgPounds,
            water_gallons:         avgWaterGallons,
            water_cost_per_gallon: avgWaterCostPerGallon,
            water_total_cost:      avgWaterGallons * avgWaterCostPerGallon,
            gallon_per_lb:         avgPounds > 0 ? (avgWaterGallons / avgPounds) : 0,
            gas_therms:            avgGasTherms,
            gas_cost_per_therm:    avgGasCostPerTherm,
            gas_total_cost:        avgGasTherms * avgGasCostPerTherm,
            therm_per_lb:          avgPounds > 0 ? (avgGasTherms / avgPounds) : 0,
            electric_kwh:          avgElectricKwh,
            electric_cost_per_kwh: avgElectricCostPerKwh,
            electric_total_cost:   avgElectricKwh * avgElectricCostPerKwh,
            kwh_per_lb:            avgPounds > 0 ? (avgElectricKwh / avgPounds) : 0
        };
    };

    // Format number with commas
    const formatNumber = (num, decimals = 0) => {
        if (num === null || num === undefined) return '-';
        return parseFloat(num).toLocaleString('en-US', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
        });
    };

    // Format month for display (e.g., "March")
    const formatMonth = (dateString) => {
        if (!dateString) return '-';
        const dateStr = typeof dateString === 'string' ? dateString : dateString.toISOString();
        const [year, month] = dateStr.substring(0, 10).split('-');
        const date = new Date(parseInt(year), parseInt(month) - 1, 1);
        return date.toLocaleDateString('en-US', { month: 'long' });
    };

    const averages = calculateAverages();
    const selectedPlantName = plants.find(p => p.plant_id.toString() === selectedPlant)?.plant_name || '';

    return (
        <div className="utilities-report-view-container">
            <div className="report-header">
                <div className="header-left">
                    <h2>{selectedPlantName || 'Utilities Report'}</h2>
                    <p className="subtitle">6 Month View</p>
                </div>

                {/* ── Controls: Print + Export buttons + selectors ── */}
                <div className="header-controls-wrapper">
                    {/* Print and Export buttons */}
                    <div className="report-actions">
                        <button
                            onClick={() => window.print()}
                            className="btn-print"
                            disabled={loading || reportData.length === 0}
                        >
                            🖨️ Print
                        </button>
                        <button
                            onClick={handleExportExcel}
                            className="btn-export"
                            disabled={loading || reportData.length === 0}
                        >
                            📊 Export to Excel
                        </button>
                    </div>

                    {/* Plant selector and month picker */}
                    <div className="report-controls">
                        <div className="form-group">
                            <label htmlFor="reportPlant">Plant:</label>
                            <select
                                id="reportPlant"
                                value={selectedPlant}
                                onChange={(e) => setSelectedPlant(e.target.value)}
                                disabled={loading}
                            >
                                <option value="">Select a plant...</option>
                                {plants.map(plant => (
                                    <option key={plant.plant_id} value={plant.plant_id}>
                                        {plant.plant_name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-group">
                            <label htmlFor="reportEndingMonth">Ending Month:</label>
                            <input
                                type="month"
                                id="reportEndingMonth"
                                value={endingMonth}
                                onChange={(e) => setEndingMonth(e.target.value)}
                                disabled={loading}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {message.text && (
                <div className={`message message-${message.type}`}>
                    {message.text}
                </div>
            )}

            {loading ? (
                <div className="loading-state">
                    <div className="loading-spinner"></div>
                    <p>Loading report data...</p>
                </div>
            ) : reportData.length > 0 ? (
                <div className="report-content">
                    <div className="table-wrapper">
                        <table className="utilities-report-table">
                            <thead>
                                <tr>
                                    <th className="month-header"></th>
                                    <th>Month</th>
                                    <th>Pounds</th>
                                    {/* Water Section */}
                                    <th className="section-water">Gallons</th>
                                    <th className="section-water">Cost Gallon</th>
                                    <th className="section-water">Water * Cost</th>
                                    <th className="section-water">Gallon / LB</th>
                                    {/* Gas Section */}
                                    <th className="section-gas">Therm</th>
                                    <th className="section-gas">Cost Therm</th>
                                    <th className="section-gas">Gas * Cost</th>
                                    <th className="section-gas">Therm / LB</th>
                                    {/* Electric Section */}
                                    <th className="section-electric">KWH</th>
                                    <th className="section-electric">Cost KWH</th>
                                    <th className="section-electric">Electric * Cost</th>
                                    <th className="section-electric">KWH / LB</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr className="label-row">
                                    <td className="current-label">Current</td>
                                    <td colSpan="14"></td>
                                </tr>
                                {reportData.map((row, index) => (
                                    <tr key={index}>
                                        <td></td>
                                        <td className="month-cell">{formatMonth(row.report_month)}</td>
                                        <td>{formatNumber(row.pounds, 0)}</td>
                                        <td className="section-water">{formatNumber(row.water_gallons, 0)}</td>
                                        <td className="section-water">{formatNumber(row.water_cost_per_gallon, 4)}</td>
                                        <td className="section-water">{formatNumber(row.water_total_cost, 0)}</td>
                                        <td className="section-water">{formatNumber(row.gallon_per_lb, 4)}</td>
                                        <td className="section-gas">{formatNumber(row.gas_therms, 0)}</td>
                                        <td className="section-gas">{formatNumber(row.gas_cost_per_therm, 4)}</td>
                                        <td className="section-gas">{formatNumber(row.gas_total_cost, 0)}</td>
                                        <td className="section-gas">{formatNumber(row.therm_per_lb, 4)}</td>
                                        <td className="section-electric">{formatNumber(row.electric_kwh, 0)}</td>
                                        <td className="section-electric">{formatNumber(row.electric_cost_per_kwh, 4)}</td>
                                        <td className="section-electric">{formatNumber(row.electric_total_cost, 0)}</td>
                                        <td className="section-electric">{formatNumber(row.kwh_per_lb, 4)}</td>
                                    </tr>
                                ))}
                                {/* 6 Month Average Row */}
                                {averages && (
                                    <tr className="average-row">
                                        <td></td>
                                        <td className="avg-label">6 MO AVG</td>
                                        <td><strong>{formatNumber(averages.pounds, 0)}</strong></td>
                                        <td className="section-water"><strong>{formatNumber(averages.water_gallons, 0)}</strong></td>
                                        <td className="section-water"><strong>{formatNumber(averages.water_cost_per_gallon, 4)}</strong></td>
                                        <td className="section-water"><strong>${formatNumber(averages.water_total_cost, 0)}</strong></td>
                                        <td className="section-water"><strong>{formatNumber(averages.gallon_per_lb, 4)}</strong></td>
                                        <td className="section-gas"><strong>{formatNumber(averages.gas_therms, 0)}</strong></td>
                                        <td className="section-gas"><strong>{formatNumber(averages.gas_cost_per_therm, 4)}</strong></td>
                                        <td className="section-gas"><strong>${formatNumber(averages.gas_total_cost, 0)}</strong></td>
                                        <td className="section-gas"><strong>{formatNumber(averages.therm_per_lb, 4)}</strong></td>
                                        <td className="section-electric"><strong>{formatNumber(averages.electric_kwh, 0)}</strong></td>
                                        <td className="section-electric"><strong>{formatNumber(averages.electric_cost_per_kwh, 4)}</strong></td>
                                        <td className="section-electric"><strong>${formatNumber(averages.electric_total_cost, 0)}</strong></td>
                                        <td className="section-electric"><strong>{formatNumber(averages.kwh_per_lb, 4)}</strong></td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                <div className="empty-state">
                    <p>No data available. Please enter data in the Utilities Data Entry tab first.</p>
                </div>
            )}
        </div>
    );
}

export default UtilitiesReportView;
